import { useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconChevron } from '../icons/WizardIcons';

export type SearchableMultiSelectOption = {
  id: string;
  label: string;
};

type Props = {
  id?: string;
  options: SearchableMultiSelectOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  onSearch: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  hasError?: boolean;
  'aria-label'?: string;
  emptyMessage?: string;
  /** Labels for selected ids not present in the current options list. */
  selectedOptionLabels?: Record<string, string>;
  searchDebounceMs?: number;
};

/** Multi-select with inline search input and portaled options menu. */
export default function WizardSearchableMultiSelect({
  id,
  options,
  value,
  onChange,
  onSearch,
  placeholder = 'Search and select…',
  searchPlaceholder,
  disabled = false,
  loading = false,
  hasError = false,
  'aria-label': ariaLabel,
  emptyMessage = 'No data found',
  selectedOptionLabels = {},
  searchDebounceMs = 300,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  const inputPlaceholder = searchPlaceholder ?? placeholder;

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const optionMap = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const selected = useMemo(
    () =>
      value
        .map((vid) => {
          const fromOptions = optionMap.get(vid);
          if (fromOptions) return fromOptions;
          const label = selectedOptionLabels[vid];
          return label ? { id: vid, label } : null;
        })
        .filter((o): o is SearchableMultiSelectOption => Boolean(o)),
    [value, optionMap, selectedOptionLabels],
  );

  const available = useMemo(
    () => options.filter((o) => !value.includes(o.id)),
    [options, value],
  );

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const updatePos = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (
        !rootRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery('');
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchRef.current(query);
    }, searchDebounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, query, searchDebounceMs]);

  const remove = (optionId: string) => {
    onChange(value.filter((v) => v !== optionId));
  };

  const add = (optionId: string) => {
    if (!optionId || value.includes(optionId)) return;
    onChange([...value, optionId]);
    setQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const openField = () => {
    if (disabled) return;
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const menu =
    open && !disabled && menuPos
      ? ReactDOM.createPortal(
          <ul
            ref={menuRef}
            id={listId}
            className="wizard-sel-menu ms-chips-menu ms-chips-menu--portal"
            role="listbox"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxWidth: menuPos.width,
              zIndex: 1050,
              overflowX: 'hidden',
            }}
          >
            {available.length > 0
              ? available.map((opt) => (
                  <li key={opt.id}>
                    <button
                      type="button"
                      role="option"
                      title={opt.label}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => add(opt.id)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))
              : !loading ? (
                  <li>
                    <span className="ms-chips-empty">{emptyMessage}</span>
                  </li>
                ) : null}
            {loading ? (
              <li>
                <span className="ms-chips-empty">Loading…</span>
              </li>
            ) : null}
          </ul>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`wizard-sel-wrap ms-chips-searchable${hasError ? ' inv' : ''}${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
      >
        <div
          className="ms-chips-inner ms-chips-inner--searchable"
          onMouseDown={(e) => {
            if (disabled) return;
            if ((e.target as HTMLElement).closest('.ms-chip button')) return;
            e.preventDefault();
            openField();
          }}
        >
          <div className="ms-chips-tags">
            {selected.map((opt) => (
              <span key={opt.id} className="ms-chip">
                {opt.label}
                {!disabled ? (
                  <button
                    type="button"
                    aria-label={`Remove ${opt.label}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(opt.id);
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))}
            <input
              ref={inputRef}
              id={id}
              type="text"
              className="ms-chips-query"
              value={query}
              disabled={disabled}
              placeholder={selected.length === 0 ? inputPlaceholder : ''}
              aria-label={ariaLabel}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              onFocus={() => {
                if (disabled) return;
                setOpen(true);
              }}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
                if (e.key === 'Backspace' && !query && value.length > 0) {
                  onChange(value.slice(0, -1));
                }
              }}
            />
          </div>
        </div>
        <button
          type="button"
          className="wizard-sel-chev-btn"
          tabIndex={-1}
          aria-label="Show options"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (disabled) return;
            if (open) {
              setOpen(false);
              setQuery('');
              return;
            }
            openField();
          }}
        >
          <IconChevron expanded={open} />
        </button>
      </div>
      {menu}
    </>
  );
}
